(() => {
  if (customElements.get('fcn-cart-tools')) return;
  // Keep the pinned checkout above the draft preview bar. This observes the
  // visible outer container height; Shopify's iframe itself fills the viewport.
  if (typeof ResizeObserver !== 'undefined' && typeof MutationObserver !== 'undefined') {
    const watchPreview = (frame) => {
      const container = frame.parentElement || frame;
      const measure = () => document.documentElement.style.setProperty('--fcn-preview-bar-height', `${container.getBoundingClientRect().height}px`);
      const resize = new ResizeObserver(measure);
      resize.observe(container);
      resize.observe(frame);
      measure();
    };
    const preview = document.getElementById('PBarNextFrame');
    if (preview) watchPreview(preview);
    else {
      const observer = new MutationObserver(() => {
        const frame = document.getElementById('PBarNextFrame');
        if (frame) { observer.disconnect(); watchPreview(frame); }
      });
      observer.observe(document.body, { childList: true });
    }
  }
  // Memory is scoped to this loaded page. Initial/repeated renders never celebrate.
  const shippingStates = new Map();
  class FadlashShipping extends HTMLElement {
    connectedCallback() {
      const threshold = Number(this.dataset.threshold);
      const total = Number(this.dataset.total);
      const key = this.dataset.country + ':' + this.dataset.currency + ':' + threshold;
      const reached = threshold > 0 && Number.isFinite(total) && total >= threshold;
      const previous = shippingStates.get(key);
      this.dataset.reached = String(reached);
      shippingStates.set(key, reached);
      if (previous === false && reached && this.dataset.celebrate === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.classList.add('fcn-celebrate');
        this.timer = setTimeout(() => this.classList.remove('fcn-celebrate'), 1300);
      }
    }
    disconnectedCallback() { clearTimeout(this.timer); }
  }
  if (!customElements.get('fcn-shipping')) customElements.define('fcn-shipping', FadlashShipping);
  // An emptied native cart is a real below-target state even though its panel is hidden.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.cartData?.item_count === 0) shippingStates.forEach((value, key) => shippingStates.set(key, false));
    });
  }
  let busy = false;
  const targets = () => {
    const sections = [];
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const element = document.getElementById(id);
      if (element) sections.push({ id: element.dataset.id, target: `#${id} .js-contents`, source: `#${id} .js-contents` });
    }
    if (document.querySelector('cart-drawer')) sections.push({ id: 'cart-drawer', target: '#CartDrawer', source: '#CartDrawer' });
    sections.push({ id: 'cart-icon-bubble', target: '#cart-icon-bubble', source: '.shopify-section' });
    if (document.getElementById('cart-live-region-text')) sections.push({ id: 'cart-live-region-text', target: '#cart-live-region-text', source: '.shopify-section' });
    return sections.slice(0, 5);
  };
  const readSections = async (sections) => {
    const response = await fetch(`${routes.cart_url}?sections=${sections.map((section) => section.id).join(',')}`, { cache: 'no-store' });
    if (!response.ok) throw new Error('The cart view could not be refreshed. Please try Refresh cart.');
    return response.json();
  };
  const render = (html, sections) => {
    // Validate every requested fragment before changing any displayed amount.
    const replacements = sections.map((section) => {
      const source = typeof html[section.id] === 'string'
        ? new DOMParser().parseFromString(html[section.id], 'text/html').querySelector(section.source)
        : null;
      if (!source) throw new Error('The cart view could not be refreshed. Please try Refresh cart.');
      return { ...section, html: source.innerHTML };
    });
    const drawer = document.querySelector('cart-drawer');
    const wasOpen = drawer?.classList.contains('active');
    const scrollTop = drawer?.querySelector('cart-drawer-items')?.scrollTop || 0;
    for (const replacement of replacements) {
      const target = document.querySelector(replacement.target);
      if (target) target.innerHTML = replacement.html;
    }
    const pageItems = document.querySelector('cart-items');
    const empty = !document.querySelector('#main-cart-items .cart-item, #CartDrawer .cart-item');
    pageItems?.classList.toggle('is-empty', empty);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', empty);
    drawer?.classList.toggle('is-empty', empty);
    if (drawer) {
      drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
      if (wasOpen) {
        const scroller = drawer.querySelector('cart-drawer-items');
        if (scroller) scroller.scrollTop = scrollTop;
        if (typeof trapFocus === 'function') trapFocus(drawer.querySelector('.drawer__inner'), drawer.querySelector('.drawer__close'));
      }
    }
  };
  class FadlashCartTools extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('change', (event) => {
        // Recommendation controls must not enter Dawn's cart-line quantity handler.
        event.stopPropagation();
        const card = event.target.closest('[data-fcn-recommendation]');
        if (!card) return;
        const variant = card.querySelector('[data-fcn-variant]');
        const chosen = variant.selectedOptions?.[0] || variant;
        // Server-formatted price groups are updated by fadlash-price.js.
        card.querySelector('[data-fcn-add]').disabled = Boolean(chosen.disabled || !variant.value);
      });
      this.addEventListener('click', (event) => {
        const add = event.target.closest('[data-fcn-add]');
        const remove = event.target.closest('[data-fcn-remove-code]');
        const refresh = event.target.closest('[data-fcn-refresh]');
        if (!add && !remove && !refresh) return;
        event.preventDefault();
        event.stopPropagation();
        if (refresh) return this.refresh();
        if (remove) return this.mutate(routes.cart_update_url, { discount: '' }, null, 'Discount code cleared.');
        const card = add.closest('[data-fcn-recommendation]');
        const variant = card.querySelector('[data-fcn-variant]');
        const chosen = variant.selectedOptions?.[0] || variant;
        if (chosen.disabled || !variant.value) return;
        this.mutate(routes.cart_add_url, { items: [{ id: Number(variant.value), quantity: 1 }] }, card);
      });
      this.addEventListener('submit', (event) => {
        if (!event.target.matches('[data-fcn-discount-form]')) return;
        event.preventDefault();
        event.stopPropagation();
        const code = event.target.elements.discount.value.trim();
        if (!code) return this.message('Enter a discount code.');
        this.mutate(routes.cart_update_url, { discount: code }, null, `Code ${code} checked by Shopify.`, code);
      });
    }
    message(text, card, refreshNeeded = false) {
      const output = card?.querySelector('[data-fcn-card-error]') || this.querySelector('[data-fcn-code-status]')
        || document.querySelector('[data-fcn-cart-status]');
      if (!output) return;
      output.hidden = false;
      output.textContent = text;
      if (refreshNeeded) {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.fcnRefresh = '';
        button.textContent = 'Refresh cart';
        output.append(' ', button);
      }
    }
    async refresh() {
      if (busy || document.querySelector('.cart__items--disabled')) return;
      busy = true;
      try { const sections = targets(); render(await readSections(sections), sections); }
      catch (error) { this.message(error.message, null, true); }
      finally { busy = false; }
    }
    async mutate(url, input, card, successMessage, code) {
      if (busy || document.querySelector('.cart__items--disabled')) return;
      busy = true;
      this.message('', card);
      const locked = [...document.querySelectorAll('cart-items input[name="updates[]"], cart-items quantity-input button, cart-drawer-items input[name="updates[]"], cart-drawer-items quantity-input button, fcn-cart-tools button, fcn-cart-tools select, [name="checkout"]')].map((element) => ({ element, disabled: element.disabled }));
      locked.forEach(({ element }) => { element.disabled = true; });
      let changed = false;
      try {
        const sections = targets();
        const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ ...input, sections: sections.map((section) => section.id), sections_url: window.location.pathname }) });
        const data = await response.json();
        if (!response.ok || data.status || data.errors) {
          throw new Error(typeof data.description === 'string' ? data.description : typeof data.errors === 'string' ? data.errors : typeof data.message === 'string' ? data.message : 'Shopify could not update this cart.');
        }
        changed = true;
        let html = data.sections;
        if (!html || sections.some((section) => !html[section.id])) html = await readSections(sections);
        render(html, sections);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: data });
        if (code || successMessage) {
          const checked = data.discount_codes?.find((discount) => discount.code.toLowerCase() === code?.toLowerCase());
          const message = checked?.applicable === false ? 'Shopify marked this code as not applicable to this cart.' : successMessage;
          document.querySelectorAll('[data-fcn-code-status]').forEach((status) => { status.textContent = message; });
        } else {
          document.querySelectorAll('[data-fcn-cart-status]').forEach((status) => { status.textContent = 'Added to your cart.'; });
        }
      } catch (error) {
        // No optimistic totals. Failed adds never change prices in the UI.
        this.message(changed ? `Shopify updated the cart, but its view could not refresh. ${error.message}` : error.message, card, changed);
      } finally {
        locked.forEach(({ element, disabled }) => { if (element.isConnected) element.disabled = disabled; });
        busy = false;
      }
    }
  }
  customElements.define('fcn-cart-tools', FadlashCartTools);
})();
